/**
 * Socket.IO client wrapper for the Mirlo Webchat Widget.
 *
 * Connects to the `/webchat` namespace with the session token obtained
 * from the handshake.  All socket event wiring is centralized here so
 * the rest of the app never imports socket.io-client directly.
 *
 * Config decisions:
 *   - transports: websocket first, fall back to polling (better latency + works
 *     through most proxies)
 *   - reconnectionAttempts: 5  (after 5 failures we surface a "disconnected"
 *     state to the user; they can trigger a manual reconnect)
 *   - reconnectionDelay: 1000 ms initial, with default exponential backoff
 */

import { io, type Socket } from 'socket.io-client';
import type { AgentMessageEvent, VisitorDetails } from './api-types';
import type { PageContext } from '../lib/page-context';
import type { VisitorContext } from '../lib/visitor-context';

export type ConnectionStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected';

export interface SocketErrorEvent {
  message: string;
  limit_type?: string;
  retry_after_seconds?: number;
  retry?: boolean;
}

export interface AvailabilityChangedPayload {
  status: 'online' | 'offline';
  /** ISO-8601 timestamp of when the business next becomes available. */
  next_available_at?: string | null;
}

/**
 * Backend-emitted error for `offline_message` submissions. `code` discriminates
 * the failure so the widget can render the right UX (missing field, bad config,
 * server error). `fields` is present when `code === 'missing_required_fields'`.
 */
export interface OfflineMessageErrorEvent {
  code:
    | 'missing_required_fields'
    | 'no_required_fields_configured'
    | 'invalid_payload'
    | 'empty_payload'
    | 'internal_error'
    | (string & {});
  message?: string;
  fields?: string[];
}

export interface WebchatSocketHandlers {
  onAgentMessage: (msg: AgentMessageEvent) => void;
  onAck: (payload: { ok: boolean }) => void;
  onErrorEvent: (err: SocketErrorEvent) => void;
  onWidgetDisabled: (payload: { reason: string }) => void;
  onStatusChange: (status: ConnectionStatus) => void;
  /** Real-time availability change (agent went online/offline, schedule boundary). */
  onAvailabilityChanged: (payload: AvailabilityChangedPayload) => void;
  /** Backend confirms the offline form was persisted. */
  onOfflineMessageAck: (payload: { ok: boolean }) => void;
  /** Backend rejected the offline form (validation or internal failure). */
  onOfflineMessageError: (err: OfflineMessageErrorEvent) => void;
}

export interface WebchatSocketController {
  /**
   * @param attachmentToken Receipt from the upload endpoint. The server
   * resolves the file from it; the widget never sends a storage location.
   * @param pageContext Current page context (URL, title, OG, structured data, visible text).
   */
  sendUserMessage: (
    text: string,
    attachmentToken?: string,
    pageContext?: PageContext,
  ) => void;
  sendVisitorDetails: (details: VisitorDetails) => void;
  /**
   * Hint the backend that the visitor is (probably) leaving — sent on
   * `beforeunload` and `visibilitychange: hidden`. The backend uses it
   * to compress its debounce grace on the pending disconnect from ~15s
   * to ~2s, so the operator sees the departure quickly on real closes
   * instead of waiting out the network-hiccup absorber. See
   * mirlo-messages-service #804 for the server-side contract.
   *
   * Fire-and-forget — losing the emit (browser tears down transport,
   * network flakes, no server) is safe: the backend falls back to the
   * default grace and eventually emits `visitor_left` from the plain
   * disconnect signal.
   */
  sendVisitorIntentLeave: () => void;
  /**
   * Submit an offline contact form when no agents are available.
   * The backend creates a ticket/message for follow-up.
   */
  sendOfflineMessage: (data: Record<string, string>) => void;
  disconnect: () => void;
}

export function connectWebchatSocket(
  apiBaseUrl: string,
  sessionToken: string,
  handlers: WebchatSocketHandlers,
  attribution?: Record<string, unknown>,
  ticketRef?: string,
  pageContext?: PageContext,
  visitorContext?: VisitorContext,
): WebchatSocketController {
  const socket: Socket = io(`${apiBaseUrl}/webchat`, {
    auth: {
      token: sessionToken,
      ...(attribution && { attribution }),
      ...(ticketRef && { ticket_ref: ticketRef }),
      ...(pageContext && { page_context: pageContext }),
      // Static auth object: socket.io resends it on every reconnection.
      ...(visitorContext && { visitor_context: visitorContext }),
    },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  // ── connection lifecycle ──────────────────────────────────────────────────
  handlers.onStatusChange('connecting');

  socket.on('connect', () => {
    handlers.onStatusChange('connected');
  });

  socket.on('disconnect', () => {
    handlers.onStatusChange('disconnected');
  });

  socket.on('reconnect_attempt', () => {
    handlers.onStatusChange('reconnecting');
  });

  socket.on('reconnect', () => {
    handlers.onStatusChange('connected');
  });

  socket.on('reconnect_failed', () => {
    handlers.onStatusChange('disconnected');
  });

  // ── application events ────────────────────────────────────────────────────
  socket.on('agent_message', (msg: AgentMessageEvent) => {
    handlers.onAgentMessage(msg);
  });

  socket.on('message_received', (payload: { ok: boolean }) => {
    handlers.onAck(payload);
  });

  socket.on('error', (err: SocketErrorEvent) => {
    handlers.onErrorEvent(err);
  });

  socket.on('widget_disabled', (payload: { reason: string }) => {
    handlers.onWidgetDisabled(payload);
  });

  socket.on('availability_changed', (payload: AvailabilityChangedPayload) => {
    handlers.onAvailabilityChanged(payload);
  });

  socket.on('offline_message_received', (payload: { ok: boolean }) => {
    handlers.onOfflineMessageAck(payload);
  });

  socket.on('offline_message_error', (err: OfflineMessageErrorEvent) => {
    handlers.onOfflineMessageError(err);
  });

  // ── controller ────────────────────────────────────────────────────────────
  return {
    sendUserMessage(
      text: string,
      attachmentToken?: string,
      pageContext?: PageContext,
    ) {
      socket.emit('user_message', {
        text,
        ...(attachmentToken ? { attachmentToken } : {}),
        ...(pageContext ? { page_context: pageContext } : {}),
      });
    },
    sendVisitorDetails(details: VisitorDetails) {
      // socket.io buffers emits until connected, so this is safe even if the
      // socket is still mid-handshake when the pre-chat form is submitted.
      socket.emit('visitor_details', details);
    },
    sendVisitorIntentLeave() {
      // Only worth sending when the socket is actually connected — a
      // buffered emit that never flushes because the transport is dying
      // helps nobody. If we're already offline, the backend will detect
      // the disconnect on its own and apply the default grace.
      if (!socket.connected) return;
      socket.emit('visitor_intent_leave');
    },
    sendOfflineMessage(data: Record<string, string>) {
      socket.emit('offline_message', data);
    },
    disconnect() {
      socket.disconnect();
    },
  };
}
